import { mentionsDoctorName } from "./chatDoctorNames";
import { removeUnsolicitedAssistantIntroduction } from "./chatIdentity";

export type ConversationTurn = { role: string; content: string };
export type ConversationLanguage = "ar" | "en" | "other";

const PAYMENT = /\b(?:payments?|pay|installments?|instalments?|tabby|tamara|financing|credit card|debit card|cash|apple pay)\b|دفع|تقسيط|[أا]قساط|تابي|تمارا|فيزا|ماستركارد|كاش/i;
const SHORT_ACK = /^(?:ok(?:ay)?|yes|no|thanks?(?: you)?|sure|fine|great|\d+(?:[.,]\d+)?\s*(?:kg|cm|m)?|prp|dhi|fue|nad\+?)\s*[.!?]*$/i;

export function conversationLanguage(history: ConversationTurn[], fallback: ConversationLanguage = "ar"): ConversationLanguage {
  for (const turn of [...history].reverse()) {
    if (turn.role !== "user") continue;
    const text = turn.content.trim();
    if (!text || SHORT_ACK.test(text)) continue;
    const arabic = (text.match(/[\u0621-\u064a]/g) || []).length;
    const latin = (text.match(/[a-z]/gi) || []).length;
    if (/[\u0400-\u04ff\u4e00-\u9fff\u3040-\u30ff\uac00-\ud7af\u0900-\u097f]/.test(text)) return "other";
    if (arabic > 0 && arabic >= latin) return "ar";
    if (latin > 0) return "en";
  }
  return fallback;
}

export function askedAboutPayment(history: ConversationTurn[]): boolean {
  const latest = history.filter(t => t.role === "user").at(-1)?.content ?? "";
  if (/\b(?:don't|do not|no|not interested in)\s+(?:want\s+)?(?:installments?|instalments?|financing|payment plans?)|لا (?:أريد|اريد|[أا]حتاج) (?:تقسيط|[أا]قساط)/i.test(latest)) return false;
  // A price, quote or discount question alone is not a request for financing.
  return PAYMENT.test(latest);
}

export function replyUnits(text: string): string[] {
  const chunks = text.trim().split(new RegExp("(?<=[!?؟])\\s+|(?<!\\bDr\\.)(?<!د\\.)(?<=\\.)\\s+(?=[\\p{L}])|\\n+", "iu")).filter(Boolean);
  const merged: string[] = [];
  for (let i = 0; i < chunks.length; i++) {
    if (/^(?:Dr|د)\.$/i.test(chunks[i]) && chunks[i + 1]) merged.push(`${chunks[i++]} ${chunks[i]}`);
    else merged.push(chunks[i].trim());
  }
  return merged;
}

function normalized(text: string): string {
  return text.toLowerCase().normalize("NFKC").replace(/[\u064b-\u065f\u0640]/g, "")
    .replace(/[أإآ]/g, "ا").replace(/ى/g, "ي").replace(new RegExp("[^\\p{L}\\p{N}\\s]", "gu"), " ").replace(/\s+/g, " ").trim();
}

const QUESTION_TOPICS: Array<[string, RegExp]> = [
  ["height", /\bheight\b|طول/i], ["weight", /\bweight\b|وزن/i],
  ["area", /\b(?:area|areas|body part)\b|منطق|مكان|جزء/i],
  ["phone", /\b(?:phone|number|mobile|contact details)\b|رقم|جوال|هاتف/i],
  ["name", /\byour name\b|اسم/i],
  ["booking", /\b(?:book|appointment|consultation|schedule|visit)\b|حجز|موعد|استشار/i],
  ["timing", /\b(?:morning|afternoon|evening|when|preferred time)\b|صباح|مساء|متي|توقيت/i],
  ["previous-treatment", /\b(?:tried|previous|before|first time)\b|جرب|سبق|اول مرة/i],
];

function isQuestionOrRequest(text: string): boolean {
  return /[?؟]/.test(text) || /^(?:[-*]\s*)?(?:could you|can you|would you|what|which|when|how|please (?:share|provide|tell)|tell me|share your|may I|هل|كم|ممكن|متى|ما |أي |شو|وين|يرجى (?:تزويد|مشاركة))/i.test(text.trim());
}

function topics(text: string): string[] {
  const value = normalized(text);
  return QUESTION_TOPICS.filter(([, test]) => test.test(value)).map(([name]) => name);
}

export function isRepeatedQuestion(candidate: string, history: ConversationTurn[]): boolean {
  if (!isQuestionOrRequest(candidate)) return false;
  const question = normalized(candidate);
  const keys = topics(candidate);
  for (const prior of history.filter(t => t.role === "assistant").flatMap(t => replyUnits(t.content)).filter(isQuestionOrRequest)) {
    const past = normalized(prior);
    if (past === question) return true;
    if (keys.length && topics(prior).some(key => keys.includes(key))) return true;
    const a = new Set(question.split(" ").filter(w => w.length > 2));
    const b = new Set(past.split(" ").filter(w => w.length > 2));
    const common = Array.from(a).filter(w => b.has(w)).length;
    if (common >= 3 && common / Math.max(a.size, b.size) >= 0.7) return true;
  }
  // Do not request facts volunteered before the bot had a chance to ask.
  const users = history.filter(t => t.role === "user").map(t => t.content).join(" ");
  if (keys.includes("height") && /\bheight\b|\d\s*(?:cm|سم)|طول/i.test(users)) return true;
  if (keys.includes("weight") && /\bweight\b|\d\s*(?:kg|كج|كيلو)|وزن/i.test(users)) return true;
  if (keys.includes("area") && /\btummy|abdomen|sides|waist|back|chin|360\b|بطن|خواصر|ذقن|وجه/i.test(users)) return true;
  if (keys.includes("phone") && /(?:\+?[\d٠-٩۰-۹][\d٠-٩۰-۹\s-]{8,}|(?:phone|mobile|رقمي|جوالي)\s*[:=]?\s*[\d٠-٩۰-۹])/i.test(users)) return true;
  if (keys.includes("name") && /\bmy name(?: is)?\b|\bi(?: am|'m)\s+(?!(?:interested|looking|asking|thinking|not|unsure|considering|worried)\b)[a-z]+\b|اسمي|[إا]سمي/i.test(users)) return true;
  return false;
}

/** Presentation filter for assistant text only; never rewrite visitor or archive records. */
export function doctorNameFreeText(text: string, language: ConversationLanguage = "ar"): string {
  const kept = replyUnits(text).filter(unit => !mentionsDoctorName(unit)).join(" ").trim();
  return kept || (language === "en" ? "The relevant clinic team can help with your enquiry." : "يمكن للفريق المختص مساعدتك في استفسارك.");
}

export function requestedBooking(history: ConversationTurn[]): boolean {
  const latest = history.filter(turn => turn.role === "user").at(-1)?.content ?? "";
  const priorAssistant = history.filter(turn => turn.role === "assistant").at(-1)?.content ?? "";
  // Questions about an existing booking still need a factual answer, not a new-booking shortcut.
  if (/\b(?:status|reschedul\w*|cancel\w*|existing|already booked|my (?:booking|appointment))\b|حالة (?:الحجز|حجزي|الموعد)|حجزي|موعدي|(?:تغيير|تعديل|إلغاء|الغاء) (?:الحجز|الموعد)|حجزت/i.test(latest)) return false;
  if (/\b(?:don't|do not|not ready to|not looking to|not interested in)\s+(?:want to\s+)?(?:book|schedule)|\bno\s+(?:booking|appointment)|لا (?:أريد|اريد) (?:الحجز|حجز)|(?:ما|مو) (?:أبي|ابي|ابغى|أبغى) (?:احجز|أحجز|حجز)/i.test(latest)) return false;
  const accepted = /^(?:yes(?: please)?|ok(?:ay)?|sure|نعم|تمام|حاضر|اجل)[.!؟\s]*$/i.test(normalized(latest))
    && /\b(?:book|booking|appointment|schedule|callback|contact you|call you)\b|حجز|موعد|نتواصل معك|يتواصل معك/i.test(priorAssistant);
  return accepted || /\b(?:book|booking|appointment|schedule|callback|call me)\b|حجز|موعد|احجز|أحجز|اتصل بي|تواصل معي/i.test(latest);
}

export function contactDetailsRequest(history: ConversationTurn[], fallback: ConversationLanguage = "ar"): string | null {
  const latest = history.filter(turn => turn.role === "user").at(-1)?.content ?? "";
  const users = history.filter(turn => turn.role === "user");
  const lastAssistant = history.filter(turn => turn.role === "assistant").at(-1)?.content ?? "";
  if (users.some(turn => /\b(?:no calls|do not contact|don't contact|don't call|do not call|not interested|prefer not|won't share|do not want to share|don't want to share|no thanks)\b|لا (?:اريد|أريد|احتاج|أحتاج) (?:التواصل|اتصال|حجز)|لا تتصل|ما ابغى|ما أبي|لن (?:اعطي|أعطي|أشارك)/i.test(turn.content))) return null;
  if (history.some(turn => turn.role === "assistant" && /contact details (?:have been|are) saved|details have been saved|تم تسجيل بيانات|تم حفظ بيانات/i.test(turn.content))) return null;
  if (/emergency|chest pain|can't breathe|cannot breathe|severe bleeding|طوارئ|الم في الصدر|ألم في الصدر|ضيق تنفس|نزيف شديد/i.test(latest)) return null;
  if (/\b(?:don't|do not|not ready to)\s+(?:want to\s+)?book|لا (?:أريد|اريد) (?:الحجز|حجز)/i.test(latest)) return null;
  const interested = /\b(?:book|booking|appointment|schedule|callback|call me|interested|want|would like|need|quote|price|cost|estimate)\b|حجز|موعد|اتصل بي|تواصل معي|مهتم|اريد|أريد|ابي|أبي|ابغى|سعر|تكلف/i.test(latest);
  const accepted = /^(?:yes(?: please)?|ok(?:ay)?|sure|نعم|تمام|حاضر|اجل)[.!؟\s]*$/i.test(normalized(latest))
    && /\b(?:book|booking|appointment|callback|contact you|call you)\b|حجز|موعد|نتواصل معك|يتواصل معك/i.test(lastAssistant);
  if (!interested && !accepted) return null;
  const lang = conversationLanguage(history, fallback);
  if (lang === "other") return null;
  const nameQuestion = lang === "en" ? "May I have your name?" : "ممكن اسمك؟";
  const phoneQuestion = lang === "en" ? "May I have your phone number?" : "ممكن رقم جوالك؟";
  const needName = !isRepeatedQuestion(nameQuestion, history);
  const needPhone = !isRepeatedQuestion(phoneQuestion, history);
  if (!needName && !needPhone) return null;
  return lang === "en"
    ? `For clinic follow-up, may I have your ${needName && needPhone ? "name and phone number" : needName ? "name" : "phone number"}?`
    : `لمتابعة استفسارك، ممكن ${needName && needPhone ? "اسمك ورقم جوالك" : needName ? "اسمك" : "رقم جوالك"}؟`;
}

const UNVERIFIED_CLAIM = /\b(?:ideal weight|you (?:need|should) (?:to )?lose \d|definitely the most effective|guaranteed results?|instant results?|no downtime|over 15 years|thousands of patients|50,?000 patients)\b|وزن[كها]? المثالي|تحتاج (?:تنزل|تخسر) \d|نتائج (?:مضمونة|فورية)|بدون (?:فترة )?نقاهة/i;

export function refineChatReply(text: string, history: ConversationTurn[], fallback: ConversationLanguage = "ar"): string {
  text = removeUnsolicitedAssistantIntroduction(text, history);
  const allowPayment = askedAboutPayment(history);
  const latest = history.filter(t => t.role === "user").at(-1)?.content ?? "";
  const bookingRequested = requestedBooking(history);
  const contactRequest = contactDetailsRequest(history, fallback);
  // On booking intent the first question must be missing contact details, not a scheduling qualifier.
  if (bookingRequested && contactRequest) return contactRequest;
  if (!bookingRequested && /^(?:ok(?:ay)?|thanks?(?: you)?|شكرا|تمام|حاضر)[.!؟\s]*$/i.test(normalized(latest))) {
    return conversationLanguage(history, fallback) === "en" ? "You're welcome. I'm here if you need anything else." : "حاضر، أنا هنا إذا احتجت توضيحاً إضافياً.";
  }
  const surgeryContext = /\b(?:liposuction|surgery|surgeon)\b|شفط|جراح/i.test(history.filter(t => t.role === "user").slice(-4).map(t => t.content).join(" "));
  const detailed = /\b(?:details?|explain|compare|risks?|steps?|differences?)\b|تفصيل|اشرح|شرح|قارن|مخاطر|خطوات|الفرق/i.test(latest);
  const kept: string[] = [];
  let questionKept = false;
  let deferContact = false;
  for (const unit of replyUnits(text)) {
    if ((!allowPayment && PAYMENT.test(unit)) || UNVERIFIED_CLAIM.test(unit) || mentionsDoctorName(unit)) continue;
    if (surgeryContext && /Dr\.?\s*Ali\b|د\.?\s*علي|علي أحمد|علي احمد/i.test(unit)) continue;
    if (isQuestionOrRequest(unit)) {
      const keys = topics(unit);
      if (keys.some(topic => ["phone", "name"].includes(topic))) {
        if (!contactRequest || questionKept) continue;
        deferContact = true;
        questionKept = true;
        continue;
      }
      if (!bookingRequested && keys.includes("booking")) continue;
      if (questionKept || isRepeatedQuestion(unit, history)) continue;
      questionKept = true;
    }
    kept.push(unit);
    if (kept.length >= (detailed ? 5 : 3)) break;
  }
  // Restore lead collection without requiring several qualification turns or blocking the answer.
  if (contactRequest && (deferContact || !questionKept) && kept.length > 0) kept.push(contactRequest);
  const result = kept.join(" ").trim();
  if (result) return result;
  if (contactRequest && deferContact) return contactRequest;
  if (/^(?:ok(?:ay)?|thanks?(?: you)?|شكرا|شكراً|تمام|حاضر)[.!؟\s]*$/i.test(latest)) {
    return conversationLanguage(history, fallback) === "en" ? "You're welcome. I'm here if you need anything else." : "حاضر، أنا هنا إذا احتجت توضيحاً إضافياً.";
  }
  const generic = conversationLanguage(history, fallback) === "en"
    ? "The relevant clinic team can confirm those details."
    : "يمكن للفريق المختص تأكيد هذه التفاصيل.";
  return contactRequest ? `${generic} ${contactRequest}` : generic;
}

export const GENTLE_FOLLOW_UP = {
  ar: "أنا هنا إذا احتجت توضيحاً إضافياً.",
  en: "I'm here if you need any further information.",
} as const;

export function gentleFollowUp(history: ConversationTurn[], fallback: ConversationLanguage = "ar"): string | null {
  if (!history.some(t => t.role === "user")) return null;
  if (history.some(t => t.role === "assistant" && Object.values(GENTLE_FOLLOW_UP).some(s => s === t.content))) return null;
  const last = history.at(-1);
  if (!last || last.role !== "assistant" || isQuestionOrRequest(replyUnits(last.content).at(-1) || "")) return null;
  const lang = conversationLanguage(history, fallback);
  return lang === "other" ? null : GENTLE_FOLLOW_UP[lang];
}
